import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import useStatusToggle from '../../hooks/useStatusToggle';
import StatusSwitch from '../../sub_components/status_switch';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSection,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

const emptyConfigForm = {
  inventory: '',
  is_enabled: true,
  base_percentage: '1',
  point_value: '1',
  redemption_point_value: '1',
  allow_redemption_on_products: true,
  allow_redemption_on_subscriptions: false,
  min_order_amount: '0',
  max_points_per_order: '',
  expiry_days: '',
  welcome_bonus_enabled: false,
  welcome_bonus_points: '0',
};

const emptyRedemptionForm = {
  loyalty_config: '',
  redemption_type: 'PURCHASE_DISCOUNT',
  name: '',
  description: '',
  min_points_required: '100',
  max_points_allowed: '',
  max_discount_percentage: '',
  max_redemption_value: '',
  is_active: true,
};

const REDEMPTION_TYPES = [
  { value: 'OFFER', label: 'Offer Redemption' },
  { value: 'PURCHASE_DISCOUNT', label: 'Purchase Discount' },
  { value: 'DELIVERY', label: 'Free Delivery' },
  { value: 'PRODUCT', label: 'Free Product' },
];

/**
 * Hub loyalty earn + redemption config (maps to BE ViewSets:
 * loyalty-config, loyalty-redemption-config).
 * Subscription loyalty multiplier / random offer / free delivery stay on Subscription Plans.
 */
export function LoyaltyConfig() {
  const dispatch = useDispatch();
  const statusToggle = useStatusToggle();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [redemptions, setRedemptions] = useState([]);

  const [configModal, setConfigModal] = useState(false);
  const [editConfig, setEditConfig] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [configForm, setConfigForm] = useState(emptyConfigForm);

  const [redemptionModal, setRedemptionModal] = useState(false);
  const [editRedemption, setEditRedemption] = useState(false);
  const [selectedRedemptionId, setSelectedRedemptionId] = useState(null);
  const [redemptionForm, setRedemptionForm] = useState(emptyRedemptionForm);

  useEffect(() => {
    dispatch(updatepageName('loyalty_config'));
    fetchConfigs();
    fetchRedemptions();
  }, [dispatch]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const hubLabel = (inventoryId) => {
    const hub = inventoryList.find((h) => Number(h.value ?? h.id) === Number(inventoryId));
    if (!hub) return `Hub #${inventoryId}`;
    return hub.label || hub.place || hub.name || `Hub #${inventoryId}`;
  };

  const configLabel = (configId) => {
    const cfg = configs.find((c) => Number(c.id) === Number(configId));
    if (!cfg) return `Config #${configId}`;
    return `${hubLabel(cfg.inventory)} (#${cfg.id})`;
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await CustomAxios.get('loyalty-config/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setConfigs(data);
    } catch (e) {
      showToast('error', 'Error', 'Failed to load loyalty configs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRedemptions = async () => {
    try {
      const res = await CustomAxios.get('loyalty-redemption-config/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setRedemptions(data);
    } catch (e) {
      showToast('error', 'Error', 'Failed to load redemption configs');
    }
  };

  const openConfigModal = (item = null) => {
    if (item) {
      setEditConfig(true);
      setSelectedConfigId(item.id);
      setConfigForm({
        inventory: item.inventory ?? '',
        is_enabled: !!item.is_enabled,
        base_percentage: String(item.base_percentage ?? '1'),
        point_value: String(item.point_value ?? '1'),
        redemption_point_value: String(item.redemption_point_value ?? '1'),
        allow_redemption_on_products: item.allow_redemption_on_products !== false,
        allow_redemption_on_subscriptions: !!item.allow_redemption_on_subscriptions,
        min_order_amount: String(item.min_order_amount ?? '0'),
        max_points_per_order: item.max_points_per_order != null ? String(item.max_points_per_order) : '',
        expiry_days: item.expiry_days != null ? String(item.expiry_days) : '',
        welcome_bonus_enabled: !!item.welcome_bonus_enabled,
        welcome_bonus_points: String(item.welcome_bonus_points ?? '0'),
      });
    } else {
      setEditConfig(false);
      setSelectedConfigId(null);
      setConfigForm(emptyConfigForm);
    }
    setConfigModal(true);
  };

  const submitConfig = async () => {
    if (!configForm.inventory) {
      showToast('error', 'Validation', 'Select a hub / inventory');
      return;
    }
    if (!configForm.base_percentage || Number(configForm.base_percentage) < 0) {
      showToast('error', 'Validation', 'Base percentage is required');
      return;
    }

    const payload = {
      inventory: Number(configForm.inventory),
      is_enabled: !!configForm.is_enabled,
      base_percentage: Number(configForm.base_percentage),
      point_value: Number(configForm.point_value || 1),
      redemption_point_value: Number(configForm.redemption_point_value || 1),
      allow_redemption_on_products: !!configForm.allow_redemption_on_products,
      allow_redemption_on_subscriptions: !!configForm.allow_redemption_on_subscriptions,
      min_order_amount: Number(configForm.min_order_amount || 0),
      max_points_per_order: configForm.max_points_per_order
        ? Number(configForm.max_points_per_order)
        : null,
      expiry_days: configForm.expiry_days ? Number(configForm.expiry_days) : null,
      welcome_bonus_enabled: !!configForm.welcome_bonus_enabled,
      welcome_bonus_points: Number(configForm.welcome_bonus_points || 0),
    };

    try {
      setSaving(true);
      if (editConfig) {
        await CustomAxios.put(`loyalty-config/${selectedConfigId}/`, payload);
        showToast('success', 'Success', 'Loyalty config updated');
      } else {
        await CustomAxios.post('loyalty-config/', payload);
        showToast('success', 'Success', 'Loyalty config created');
      }
      setConfigModal(false);
      await fetchConfigs();
    } catch (e) {
      const detail =
        e.response?.data?.detail ||
        e.response?.data?.non_field_errors?.[0] ||
        e.response?.data?.inventory?.[0] ||
        (typeof e.response?.data === 'string' ? e.response.data : null) ||
        e.response?.data?.error ||
        'Save failed';
      showToast('error', 'Error', String(detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleConfigEnabled = async (item) => {
    const next = !item.is_enabled;
    await statusToggle.run(item.id, next, async () => {
      try {
        await CustomAxios.patch(`loyalty-config/${item.id}/`, {
          is_enabled: next,
        });
      } catch (e) {
        showToast('error', 'Error', 'Failed to update status');
        throw e;
      }
      showToast('success', 'Success', 'Status updated');
      await fetchConfigs();
    });
  };

  const deleteConfig = async (id) => {
    if (!window.confirm('Delete this loyalty config and its redemptions?')) return;
    try {
      setLoading(true);
      await CustomAxios.delete(`loyalty-config/${id}/`);
      showToast('success', 'Success', 'Deleted');
      await fetchConfigs();
      await fetchRedemptions();
    } catch (e) {
      showToast('error', 'Error', e.response?.data?.error || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const openRedemptionModal = (item = null) => {
    if (item) {
      setEditRedemption(true);
      setSelectedRedemptionId(item.id);
      setRedemptionForm({
        loyalty_config: item.loyalty_config ?? '',
        redemption_type: item.redemption_type || 'PURCHASE_DISCOUNT',
        name: item.name || '',
        description: item.description || '',
        min_points_required: String(item.min_points_required ?? '100'),
        max_points_allowed: item.max_points_allowed != null ? String(item.max_points_allowed) : '',
        max_discount_percentage:
          item.max_discount_percentage != null ? String(item.max_discount_percentage) : '',
        max_redemption_value:
          item.max_redemption_value != null ? String(item.max_redemption_value) : '',
        is_active: item.is_active !== false,
      });
    } else {
      setEditRedemption(false);
      setSelectedRedemptionId(null);
      setRedemptionForm({
        ...emptyRedemptionForm,
        loyalty_config: configs[0]?.id || '',
      });
    }
    setRedemptionModal(true);
  };

  const submitRedemption = async () => {
    if (!redemptionForm.loyalty_config) {
      showToast('error', 'Validation', 'Select a loyalty config');
      return;
    }
    if (!redemptionForm.name.trim()) {
      showToast('error', 'Validation', 'Name is required');
      return;
    }

    const payload = {
      loyalty_config: Number(redemptionForm.loyalty_config),
      redemption_type: redemptionForm.redemption_type,
      name: redemptionForm.name.trim(),
      description: redemptionForm.description || '',
      min_points_required: Number(redemptionForm.min_points_required || 0),
      max_points_allowed: redemptionForm.max_points_allowed
        ? Number(redemptionForm.max_points_allowed)
        : null,
      max_discount_percentage: redemptionForm.max_discount_percentage
        ? Number(redemptionForm.max_discount_percentage)
        : null,
      max_redemption_value: redemptionForm.max_redemption_value
        ? Number(redemptionForm.max_redemption_value)
        : null,
      is_active: !!redemptionForm.is_active,
    };

    try {
      setSaving(true);
      if (editRedemption) {
        await CustomAxios.put(`loyalty-redemption-config/${selectedRedemptionId}/`, payload);
        showToast('success', 'Success', 'Redemption rule updated');
      } else {
        await CustomAxios.post('loyalty-redemption-config/', payload);
        showToast('success', 'Success', 'Redemption rule created');
      }
      setRedemptionModal(false);
      await fetchRedemptions();
    } catch (e) {
      showToast('error', 'Error', e.response?.data?.error || e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteRedemption = async (id) => {
    if (!window.confirm('Delete this redemption rule?')) return;
    try {
      await CustomAxios.delete(`loyalty-redemption-config/${id}/`);
      showToast('success', 'Success', 'Deleted');
      fetchRedemptions();
    } catch (e) {
      showToast('error', 'Error', 'Delete failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toast ref={toast} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Loyalty Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Hub earn rates and point redemption. For subscriber 2x points, free delivery, and every-Nth-order
          random offers, configure those on Subscription Plans.
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Earn Config (Hub)" />
        <Tab label="Redemption Rules" />
      </Tabs>

      {loading && !configs.length && !redemptions.length ? (
        <GrowthPageLoader label="Loading loyalty settings…" />
      ) : null}

      {!(loading && !configs.length && !redemptions.length) && tab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <GrowthPrimaryButton startIcon={<AddIcon />} onClick={() => openConfigModal()}>
              Add Hub Loyalty Config
            </GrowthPrimaryButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hub</TableCell>
                  <TableCell>Base %</TableCell>
                  <TableCell>Earn ₹/pt</TableCell>
                  <TableCell>Redeem ₹/pt</TableCell>
                  <TableCell>Products</TableCell>
                  <TableCell>Subscriptions</TableCell>
                  <TableCell>Min Order</TableCell>
                  <TableCell>Max Pts / Order</TableCell>
                  <TableCell>Expiry Days</TableCell>
                  <TableCell>Welcome Pts</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      {loading ? 'Loading…' : 'No loyalty configs yet'}
                    </TableCell>
                  </TableRow>
                )}
                {configs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{hubLabel(row.inventory)}</TableCell>
                    <TableCell>{row.base_percentage}%</TableCell>
                    <TableCell>{row.point_value}</TableCell>
                    <TableCell>{row.redemption_point_value ?? row.point_value}</TableCell>
                    <TableCell>{row.allow_redemption_on_products !== false ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{row.allow_redemption_on_subscriptions ? 'Yes' : 'No'}</TableCell>
                    <TableCell>₹{row.min_order_amount}</TableCell>
                    <TableCell>{row.max_points_per_order ?? '—'}</TableCell>
                    <TableCell>{row.expiry_days ?? '—'}</TableCell>
                    <TableCell>
                      {row.welcome_bonus_enabled
                        ? `${row.welcome_bonus_points ?? 0}`
                        : 'Off'}
                    </TableCell>
                    <TableCell>
                      <StatusSwitch
                        checked={!!statusToggle.displayed(row.id, row.is_enabled)}
                        pending={statusToggle.isPending(row.id)}
                        onChange={() => toggleConfigEnabled(row)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openConfigModal(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteConfig(row.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!(loading && !configs.length && !redemptions.length) && tab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <GrowthPrimaryButton
              startIcon={<AddIcon />}
              onClick={() => openRedemptionModal()}
              disabled={!configs.length}
            >
              Add Redemption Rule
            </GrowthPrimaryButton>
          </Box>
          {!configs.length && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a hub earn config first, then add redemption rules.
            </Typography>
          )}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Hub Config</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Min Points</TableCell>
                  <TableCell>Max Discount %</TableCell>
                  <TableCell>Max ₹ Cap</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redemptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No redemption rules
                    </TableCell>
                  </TableRow>
                )}
                {redemptions.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{configLabel(row.loyalty_config)}</TableCell>
                    <TableCell>
                      {REDEMPTION_TYPES.find((t) => t.value === row.redemption_type)?.label ||
                        row.redemption_type}
                    </TableCell>
                    <TableCell>{row.min_points_required}</TableCell>
                    <TableCell>{row.max_discount_percentage ?? '—'}</TableCell>
                    <TableCell>{row.max_redemption_value != null ? `₹${row.max_redemption_value}` : '—'}</TableCell>
                    <TableCell>{row.is_active ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openRedemptionModal(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteRedemption(row.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Earn config modal */}
      <GrowthModal
        open={configModal}
        onClose={() => !saving && setConfigModal(false)}
        title={editConfig ? 'Edit Loyalty Config' : 'Add Loyalty Config'}
        subtitle="Hub earn rates, welcome bonus and redemption toggles"
        busy={saving}
        busyLabel={editConfig ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setConfigModal(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={submitConfig} loading={saving}>
              {editConfig ? 'Update' : 'Create'}
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={editConfig}>
                <InputLabel>Hub</InputLabel>
                <Select
                  label="Hub"
                  value={configForm.inventory}
                  onChange={(e) => setConfigForm({ ...configForm, inventory: e.target.value })}
                >
                  {inventoryList.map((hub) => (
                    <MenuItem key={hub.value ?? hub.id} value={hub.value ?? hub.id}>
                      {hub.label || hub.place || hub.name || `Hub ${hub.value ?? hub.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <GrowthFormSection title="Earn rates" />
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Base earn %"
                type="number"
                value={configForm.base_percentage}
                onChange={(e) => setConfigForm({ ...configForm, base_percentage: e.target.value })}
                helperText="Order amount × % → points (before subscription multiplier)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Earn point value (₹ per point earned)"
                type="number"
                value={configForm.point_value}
                onChange={(e) => setConfigForm({ ...configForm, point_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Redeem point value (₹ per point redeemed)"
                type="number"
                value={configForm.redemption_point_value}
                onChange={(e) =>
                  setConfigForm({ ...configForm, redemption_point_value: e.target.value })
                }
                helperText="Separate from earn rate — used at checkout redemption"
              />
            </Grid>

            <GrowthFormSection title="Redemption options" />
            <Grid item xs={12} sm={6}>
              <GrowthFormSwitch
                label="Allow redemption on products"
                checked={configForm.allow_redemption_on_products}
                onChange={(e) =>
                  setConfigForm({ ...configForm, allow_redemption_on_products: e.target.checked })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GrowthFormSwitch
                label="Allow redemption on subscriptions"
                checked={configForm.allow_redemption_on_subscriptions}
                onChange={(e) =>
                  setConfigForm({ ...configForm, allow_redemption_on_subscriptions: e.target.checked })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Min order amount"
                type="number"
                value={configForm.min_order_amount}
                onChange={(e) => setConfigForm({ ...configForm, min_order_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Max points / order"
                type="number"
                value={configForm.max_points_per_order}
                onChange={(e) =>
                  setConfigForm({ ...configForm, max_points_per_order: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Expiry days"
                type="number"
                value={configForm.expiry_days}
                onChange={(e) => setConfigForm({ ...configForm, expiry_days: e.target.value })}
              />
            </Grid>

            <GrowthFormSection title="Welcome loyalty bonus (signup + optional backfill)" />
            <Grid item xs={12} sm={6}>
              <GrowthFormSwitch
                label="Welcome bonus enabled"
                checked={configForm.welcome_bonus_enabled}
                onChange={(e) =>
                  setConfigForm({ ...configForm, welcome_bonus_enabled: e.target.checked })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Welcome bonus points"
                type="number"
                value={configForm.welcome_bonus_points}
                onChange={(e) =>
                  setConfigForm({ ...configForm, welcome_bonus_points: e.target.value })
                }
                helperText="Credited once per user (WELCOME_BONUS). Future credits only."
              />
            </Grid>
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Enabled"
                checked={configForm.is_enabled}
                onChange={(e) => setConfigForm({ ...configForm, is_enabled: e.target.checked })}
              />
            </Grid>
          </GrowthFormGrid>
      </GrowthModal>

      {/* Redemption modal */}
      <GrowthModal
        open={redemptionModal}
        onClose={() => !saving && setRedemptionModal(false)}
        title={editRedemption ? 'Edit Redemption Rule' : 'Add Redemption Rule'}
        subtitle="Points required and discount limits for checkout redemption"
        busy={saving}
        busyLabel={editRedemption ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setRedemptionModal(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={submitRedemption} loading={saving}>
              {editRedemption ? 'Update' : 'Create'}
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Loyalty config</InputLabel>
                <Select
                  label="Loyalty config"
                  value={redemptionForm.loyalty_config}
                  onChange={(e) =>
                    setRedemptionForm({ ...redemptionForm, loyalty_config: e.target.value })
                  }
                >
                  {configs.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {hubLabel(c.inventory)} (#{c.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={redemptionForm.redemption_type}
                  onChange={(e) =>
                    setRedemptionForm({ ...redemptionForm, redemption_type: e.target.value })
                  }
                >
                  {REDEMPTION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={redemptionForm.name}
                onChange={(e) => setRedemptionForm({ ...redemptionForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Description"
                value={redemptionForm.description}
                onChange={(e) =>
                  setRedemptionForm({ ...redemptionForm, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Min points required"
                type="number"
                value={redemptionForm.min_points_required}
                onChange={(e) =>
                  setRedemptionForm({ ...redemptionForm, min_points_required: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Max points allowed"
                type="number"
                value={redemptionForm.max_points_allowed}
                onChange={(e) =>
                  setRedemptionForm({ ...redemptionForm, max_points_allowed: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Max discount %"
                type="number"
                value={redemptionForm.max_discount_percentage}
                onChange={(e) =>
                  setRedemptionForm({
                    ...redemptionForm,
                    max_discount_percentage: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Max redemption value"
                type="number"
                value={redemptionForm.max_redemption_value}
                onChange={(e) =>
                  setRedemptionForm({ ...redemptionForm, max_redemption_value: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Active"
                checked={redemptionForm.is_active}
                onChange={(e) =>
                  setRedemptionForm({ ...redemptionForm, is_active: e.target.checked })
                }
              />
            </Grid>
          </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}

export default LoyaltyConfig;
